/* istanbul ignore file */
import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import 'intersection-observer'
import Pusher from 'pusher-js'
import React from 'react'
import { createRoot } from 'react-dom/client'
import 'url-search-params-polyfill'
import { registerSW } from 'virtual:pwa-register'

import AppProviders from 'src/contexts'
import { initSegment } from 'src/hooks/useSegmentInit'
import { initZenDesk } from 'src/hooks/useZenDeskInit'

import App from './App'
import './index.css'
import configureStore from './store/configureStore'
import './tailwind.css'
import { fetchAndPopulateConfig } from './utils/config'
import { installGettextFallbacks, loadJsi18nScript } from './utils/jsi18n'

declare global {
  interface Window {
    APP_ENVIRONMENT?: 'PRODUCTION' | 'TESTING' | 'DEVELOPMENT' | 'TRAINING'
    DISABLE_TRACKING_FOR_STAFF?: 'True' | 'False' | boolean
    SOURCE_VERSION?: string
    PREVIOUS_RELEASE_TAG?: string
    USER_ID_PREFIX?: string
    process: any
    SCHEDULER_ORDER_ITEMS_LIMIT?: number
    INVOICE_XLSX_SYNC_MAX_DOCUMENTS?: number
    CUTOFF_TIME_GRACE_PERIOD_MINUTES?: number
    DATADOG_APPLICATION_ID?: string
    DATADOG_CLIENT_TOKEN?: string
    DATADOG_ENVIRONMENT?: string
    DATADOG_SERVICE_NAME?: string
    DATADOG_VERSION?: string
    DATADOG_SITE: string
    PUSHER_KEY?: string
    PUSHER_ZONE?: string
    PUSHER_CHANNEL?: string
  }
}

// process.env polyfill
if (!window.process) {
  window.process = { env: {} }
}

// ResizeObserver errors to ignore (caused by third-party widget iframes)
const IGNORED_ERROR_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
]

function initDatadog() {
  if (window.DATADOG_APPLICATION_ID && window.DATADOG_CLIENT_TOKEN) {
    datadogRum.init({
      applicationId: window.DATADOG_APPLICATION_ID,
      clientToken: window.DATADOG_CLIENT_TOKEN,
      site: window.DATADOG_SITE as any,
      service: window.DATADOG_SERVICE_NAME,
      env: window.DATADOG_ENVIRONMENT,
      version: window.DATADOG_VERSION,
      defaultPrivacyLevel: 'mask-user-input',
      trackLongTasks: false,
      trackResources: false,
      trackUserInteractions: false,
      trackViewsManually: false,
      plugins: [reactPlugin({ router: false })],
      beforeSend: event => {
        if (event.type === 'error') {
          const errorMessage = event.error?.message || ''
          if (IGNORED_ERROR_MESSAGES.some(msg => errorMessage.includes(msg))) {
            return false
          }
        }
        return true
      },
    })
  }
}

function initPusher() {
  const { PUSHER_KEY, PUSHER_ZONE, PUSHER_CHANNEL } = window
  if (PUSHER_KEY && PUSHER_ZONE && PUSHER_CHANNEL) {
    const pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_ZONE,
      forceTLS: true,
    })
    window.pusher = pusher
    window.pusherPublicChannel = pusher.subscribe(PUSHER_CHANNEL)
    // eslint-disable-next-line no-console
    console.info('[Pusher] Initialized on channel', PUSHER_CHANNEL)
  } else {
    // eslint-disable-next-line no-console
    console.warn('[Pusher] Missing PUSHER_KEY/ZONE/CHANNEL, skipping init')
  }
}

function runInit(name: string, fn: () => void) {
  try {
    fn()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[bootstrap] ${name} failed, continuing without it`, err)
  }
}

async function bootstrap() {
  await fetchAndPopulateConfig()
  runInit('Datadog', initDatadog)
  runInit('Pusher', initPusher)
  runInit('Amplitude', initSegment)
  runInit('ZenDesk', initZenDesk)
  try {
    await loadJsi18nScript()
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[jsi18n] Failed to load translation catalog, rendering untranslated')
    installGettextFallbacks()
  }

  const store = configureStore()
  const container = document.getElementById('root')
  const root = createRoot(container!)

  root.render(
    <AppProviders store={store}>
      <App />
    </AppProviders>
  )

  registerSW({ immediate: true })
}

function renderBootstrapError(error: unknown) {
  const container = document.getElementById('root')
  if (!container) return
  const root = createRoot(container)
  const message = error instanceof Error ? error.message : String(error)
  root.render(
    <div style={{ padding: '2rem', fontFamily: 'sans-serif', textAlign: 'center' }}>
      Something went wrong. Please refresh the page or try again later.
      {process.env.NODE_ENV === 'development' && (
        <pre style={{ marginTop: '1rem', textAlign: 'left', overflow: 'auto' }}>{message}</pre>
      )}
    </div>
  )
}

bootstrap().catch(err => {
  // eslint-disable-next-line no-console
  console.error('[bootstrap]', err)
  renderBootstrapError(err)
})
